import { Injectable } from "@angular/core";
import type { CV, Mat, Rect } from "@techstark/opencv-js";
import imageCompression from "browser-image-compression";
import { base64ToBlob } from "./data-matrix/utils";

@Injectable({ providedIn: "root" })
export class SignatureProcessingService {
  readonly SIGNATURE_MAX_WIDTH = 500;
  readonly MIN_LINE_LENGTH = 30;
  readonly MAX_DISTANCE_FROM_BORDER = 25; // pixels from border
  readonly MAX_ALLOWED_DEGREE = 8; // near-horizontal/vertical allowance
  readonly HOUGHLINE_RHO = 1;
  readonly HOUGHLINE_THETA = Math.PI / 180;
  readonly HOUGHLINE_THRESHOLD = 5; //Hough transform voting count
  readonly MAX_LINE_GAP = 3;

  /* eslint-disable no-console */
  async formatSignatureImage(
    signatureBase64: string | null | undefined
  ): Promise<string | null> {
    if (!signatureBase64) {
      return null;
    }

    try {
      let cv: CV;
      if (globalThis.cv) {
        cv = globalThis.cv;
      } else {
        const { default: cvReadyPromise } = await import(
          "@techstark/opencv-js"
        );
        cv = await cvReadyPromise;
      }

      const img = new Image();
      img.src = `data:image/png;base64,${signatureBase64}`;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        console.error("Could not get context.");
        return null;
      }

      const processedSignature: string | null = await new Promise<
        string | null
      >((resolve, reject) => {
        img.addEventListener("load", () =>
          this.handleImageLoad(cv, img, canvas, ctx, resolve)
        );

        img.addEventListener("error", () =>
          reject(new Error("Failed to load image"))
        );
      });

      return processedSignature;
    } catch (error) {
      console.error("Error with opencv js:", error);
      return null;
    }
  }

  async handleImageLoad(
    cv: CV,
    img: HTMLImageElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    resolve: (value: unknown) => void
  ): Promise<any> {
    try {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const source = cv.imread(canvas);
      const image = source.clone();

      const preprocessedImage = this.preProcessImage(cv, image);

      const imageMask = this.generateBackgroundMask(cv, preprocessedImage);

      //Use mask directly because we want a binary image for signature anyway
      const result = this.postProcessImage(cv, imageMask);
      if (!result) {
        resolve(null);
        return;
      }
      // Convert to canvas and base64 (PNG)
      const outputCanvas = document.createElement("canvas");
      outputCanvas.width = result.cols;
      outputCanvas.height = result.rows;
      cv.imshow(outputCanvas, result);
      const processedBase64 = (
        await imageCompression.getDataUrlFromFile(
          await imageCompression(
            new File(
              [base64ToBlob(outputCanvas.toDataURL("image/png"), "image/png")],
              "signature.png",
              { type: "image/png" }
            ),
            {
              maxSizeMB: 20,
              maxWidthOrHeight: 500,
              useWebWorker: true,
              libURL:
                "assets/browser-image-compression/browser-image-compression.js",
            }
          )
        )
      ).split(",")[1];

      // Cleanup
      source.delete();
      image.delete();
      preprocessedImage.delete();
      imageMask.delete();
      result.delete();

      resolve(processedBase64);
    } catch (error) {
      console.error("Error processing signature image:", error);
      resolve(null);
    }
  }
  /* eslint-enable no-console */

  /**
   * Removes signature box from template if it shows up for cropped image
   * @param cv open cv instance
   * @param src source image
   * @returns a cropped image without the signature box from template
   */
  private preProcessImage(cv: CV, src: Mat): Mat {
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_BGRA2GRAY);

    const mask = new cv.Mat();
    cv.adaptiveThreshold(
      gray,
      mask,
      255, //max hex value
      cv.ADAPTIVE_THRESH_GAUSSIAN_C,
      cv.THRESH_BINARY_INV,
      25, //block size, must be odd
      5
    );

    //Get straight lines
    const lines = new cv.Mat();
    cv.HoughLinesP(
      mask,
      lines,
      this.HOUGHLINE_RHO,
      this.HOUGHLINE_THETA,
      this.HOUGHLINE_THRESHOLD,
      this.MIN_LINE_LENGTH,
      this.MAX_LINE_GAP
    );

    const imgW = mask.cols;
    const yEnd = mask.rows;

    let xStart = 0;
    let xEnd = imgW;
    let yStart = 0;
    const rightThreshold = imgW - 1 - this.MAX_DISTANCE_FROM_BORDER;

    for (let i = 0; i < lines.rows; i++) {
      const { x1, y1, x2, y2, isVertical, isHorizontal } = this.computeLineInfo(
        lines,
        i
      );

      if (isVertical) {
        if (
          x1 <= this.MAX_DISTANCE_FROM_BORDER &&
          x2 <= this.MAX_DISTANCE_FROM_BORDER
        ) {
          xStart = Math.max(xStart, x1, x2); //furthest start point
        } else if (x1 >= rightThreshold && x2 >= rightThreshold) {
          xEnd = Math.min(xEnd, x1, x2); //closest end point
        }
      }

      if (
        isHorizontal &&
        y1 <= this.MAX_DISTANCE_FROM_BORDER &&
        y2 <= this.MAX_DISTANCE_FROM_BORDER
      ) {
        yStart = Math.max(yStart, y1, y2);
      }
    }

    const rect = new cv.Rect(xStart, yStart, xEnd - xStart, yEnd - yStart);
    gray.delete();
    mask.delete();
    lines.delete();
    return src.roi(rect);
  }

  private computeLineInfo(
    lines: Mat,
    index: number
  ): {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    isVertical: boolean;
    isHorizontal: boolean;
  } {
    const base = index * 4;
    const x1 = lines.data32S[base];
    const y1 = lines.data32S[base + 1];
    const x2 = lines.data32S[base + 2];
    const y2 = lines.data32S[base + 3];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const angleDeg = (Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI;
    const isVertical = angleDeg >= 90 - this.MAX_ALLOWED_DEGREE;
    const isHorizontal = angleDeg <= this.MAX_ALLOWED_DEGREE;
    return { x1, y1, x2, y2, isVertical, isHorizontal };
  }

  /**
   * Generates a background mask (white for background) for the provided image.
   * Uses Otsu thresholding with binary-inverted output to separate signature
   * from background.
   * @param cv initialized opencv instance
   * @param image Mat() object, image to apply threshold on
   * @returns Object with mask and inverted mask, binary images in grayscale colorspace
   */
  private generateBackgroundMask(cv: CV, image: Mat): Mat {
    const gray = new cv.Mat();
    cv.cvtColor(image, gray, cv.COLOR_BGRA2GRAY);

    const mask = new cv.Mat();
    cv.threshold(
      gray,
      mask,
      0,
      255, //max hex value
      cv.THRESH_BINARY_INV + cv.THRESH_OTSU
    );
    gray.delete();
    return mask;
  }

  /**
   * Smooth a binary/grayscale mask using a morphological closing operation.
   *
   * Applies cv.MORPH_CLOSE with a 2x2 ones kernel (1 iteration) to fill small
   * holes and remove noise, producing a cleaner contiguous mask.
   *
   * @param cv OpenCV.js instance
   * @param image Binary or grayscale Mat mask to smooth (typically 0/255)
   * @returns New Mat containing the smoothed mask
   */
  private smoothImage(cv: CV, image: Mat): Mat {
    const dest = new cv.Mat();
    const kernel = cv.Mat.ones(2, 2, image.type());
    const anchor = new cv.Point(-1, -1);
    cv.dilate(image, dest, kernel, anchor, 1);
    kernel.delete();
    return dest;
  }

  /**
   * Merge BGR with provided alpha mask into an RGBA image.
   * @param cv opencv instance
   * @param srcImage 3 or 4 channel opencv image, if grayscale(1 channel) then convert to BGRA
   * @param alpha binary image mask to apply to alpha channel, 0 (black) applied to alpha channel sets opacity to 0 making it transparent
   * @returns new Mat() image with transparent background
   */
  private removeBackground(cv: CV, srcImage: Mat, alpha: Mat): any {
    const rgba = new cv.MatVector();
    try {
      cv.split(srcImage, rgba);
      if (rgba.size() === 4) {
        rgba.set(3, alpha);
      } else {
        rgba.push_back(alpha); //Push alpha channel as 4th channel
      }
      const result = new cv.Mat();
      cv.merge(rgba, result);
      return result;
    } catch (error) {
      console.error("Failed to remove background:", error);
      return null;
    } finally {
      rgba.delete();
    }
  }

  /**
   * Resize image preserving aspect ratio to SIGNATURE_MAX_WIDTH(500px)
   * @param cv openCV instance
   * @param src Source Image
   * @param rect cropping region of interest
   * @returns resized image
   */
  cropAndResizeToMaxWidth(cv: CV, src: Mat, rect: Rect): Mat {
    const roiMat = src.roi(rect);
    try {
      const aspectRatio = rect.width / rect.height;
      const targetHeight = Math.round(this.SIGNATURE_MAX_WIDTH / aspectRatio);
      const dsize = new cv.Size(this.SIGNATURE_MAX_WIDTH, targetHeight);
      const resized = new cv.Mat();
      cv.resize(roiMat, resized, dsize, 0, 0, cv.INTER_AREA);
      return resized;
    } finally {
      roiMat.delete();
    }
  }

  /**
   * Post-process the image provided - smooth image, remove background and crop image
   */
  private postProcessImage(cv: CV, image: Mat): Mat | null {
    const smoothedImageMask = this.smoothImage(cv, image);
    const rect = cv.boundingRect(smoothedImageMask);

    const smoothedImage = new cv.Mat();
    cv.bitwise_not(smoothedImageMask, smoothedImage);

    const grayscaleSmoothedImageToBGR = new cv.Mat();
    cv.cvtColor(smoothedImage, grayscaleSmoothedImageToBGR, cv.COLOR_GRAY2BGR);

    const imageWithoutBackground = this.removeBackground(
      cv,
      grayscaleSmoothedImageToBGR,
      smoothedImageMask
    );

    if (!imageWithoutBackground) {
      return null;
    }

    const result = this.cropAndResizeToMaxWidth(
      cv,
      imageWithoutBackground,
      rect
    );

    smoothedImageMask.delete();
    smoothedImage.delete();
    grayscaleSmoothedImageToBGR.delete();
    imageWithoutBackground.delete();

    return result;
  }
}
